import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { useTranslation } from "react-i18next";
import magazineData from "../../api/MagazineData.json";
import "./MagazineSection.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const MagazineSection = () => {
  const { t, i18n } = useTranslation();
  const [previewImg, setPreviewImg] = useState(null);
  const modalRef = useRef(null);

  // title/issue alanı { en, tr, ar } objesi veya düz string olabilir
  const getLang = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    const lang = i18n.language?.slice(0, 2); // "en", "tr", "ar"
    return field[lang] || field.en || Object.values(field)[0] || "";
  };

  const DesignedCover = ({ mag }) => (
    <div className="mag-cover-placeholder">
      <div className="mag-ph-top">
        <span className="mag-ph-brand">M Ü K E M E L</span>
        <span className="mag-ph-tag">{t("magazine.title")}</span>
      </div>

      <div className="mag-ph-body">
        {/* Zone 1 — Baskı numarası */}
        <div className="mag-ph-edition-zone">
          <div className="mag-ph-edition-box">
            <span className="mag-ph-label">N°</span>
            <span className="mag-ph-num">
              {String(mag.edition || 1).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Zone 2 — Logo veya başlık bandı */}
        <div className="mag-ph-title-zone">
          {mag.logo ? (
            <>
              <img
                src={mag.logo}
                alt={getLang(mag.title)}
                className="mag-ph-logo"
              />
              <span className="mag-ph-logo-label">
                {t("OfficialRecognition")}
              </span>
            </>
          ) : (
            <span className="mag-ph-title">{getLang(mag.title)}</span>
          )}
        </div>

        {/* Zone 3 — Tarih + süsleme */}
        <div className="mag-ph-foot-zone">
          <span className="mag-ph-issue">{getLang(mag.issue)}</span>
          <div className="mag-ph-ornament">
            <span className="mag-ph-orn-line" />
            <span className="mag-ph-orn-diamond" />
            <span className="mag-ph-orn-line" />
          </div>
        </div>
      </div>

      <div className="mag-ph-bottom" />
    </div>
  );

  const handleCardClick = (mag) => {
    if (mag.pdfUrl) {
      window.open(mag.pdfUrl, "_blank", "noopener,noreferrer");
    } else if (mag.coverImage) {
      setPreviewImg(mag.coverImage);
      const modal = new window.bootstrap.Modal(modalRef.current);
      modal.show();
    }
  };

  if (!magazineData.magazines || magazineData.magazines.length === 0) {
    return (
      <section className="mag-section">
        <div className="mag-header">
          <span className="mag-line" />
          <h2 className="mag-heading">{t("magazine.title")}</h2>
          <span className="mag-line" />
        </div>
        <p className="mag-empty">{t("magazine.noContent")}</p>
      </section>
    );
  }

  return (
    <section className="mag-section">
      <div className="mag-header">
        <span className="mag-line" />
        <h2 className="mag-heading">{t("magazine.title")}</h2>
        <span className="mag-line" />
      </div>

      <div className="mag-swiper-wrap">
        <Swiper
          modules={[Navigation, Pagination]}
          navigation
          pagination={{ clickable: true }}
          spaceBetween={28}
          breakpoints={{
            0: { slidesPerView: 1 },
            580: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1400: { slidesPerView: 4 },
          }}
          className="mag-swiper"
        >
          {magazineData.magazines.map((mag) => (
            <SwiperSlide key={mag.id}>
              <div
                className={`mag-card ${mag.pdfUrl || mag.coverImage ? "mag-card--clickable" : ""}`}
                onClick={() => handleCardClick(mag)}
              >
                {mag.isNew && (
                  <span className="mag-badge">{t("magazine.new")}</span>
                )}

                <div className="mag-cover">
                  {mag.coverImage ? (
                    <img
                      src={mag.coverImage}
                      alt={getLang(mag.title)}
                      loading="lazy"
                    />
                  ) : mag.pdfUrl ? (
                    <div className="mag-pdf-thumb">
                      <Document
                        file={mag.pdfUrl}
                        loading={
                          <div className="mag-pdf-loading">
                            <span className="mag-pdf-spinner" />
                          </div>
                        }
                        error={<DesignedCover mag={mag} />}
                      >
                        <Page
                          pageNumber={1}
                          width={300}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      </Document>
                    </div>
                  ) : (
                    <DesignedCover mag={mag} />
                  )}

                  <div className="mag-overlay">
                    <div className="mag-overlay-inner">
                      <span className="mag-overlay-issue">
                        {getLang(mag.issue)}
                      </span>
                      <h4 className="mag-overlay-title">
                        {getLang(mag.title)}
                      </h4>
                      {mag.pdfUrl && (
                        <span className="mag-overlay-btn">
                          {t("magazine.readNow")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mag-meta">
                  <span className="mag-issue">{getLang(mag.issue)}</span>
                  <h4 className="mag-title">{getLang(mag.title)}</h4>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Image Preview Modal */}
      <div
        className="modal fade"
        tabIndex="-1"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content bg-dark border-0">
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body text-center p-3">
              {previewImg && (
                <img
                  src={previewImg}
                  alt={t("magazine.preview")}
                  className="img-fluid"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MagazineSection;
